import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [pendingAvailabilityCount, setPendingAvailabilityCount] = useState(0);
  const location = useLocation();
  const { getAuthToken } = useAuth();

  // Fetch pending event requests count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = await getAuthToken();
        const restOperation = get({
          apiName: 'ChulasAdminAPI',
          path: '/v1/admin/event-requests?status=pending',
          options: {
            headers: {
              Authorization: token,
            },
          },
        });
        const response = await restOperation.response;
        const data = await response.body.json();
        setPendingRequestsCount(data.requests?.length || 0);
      } catch (err) {
        console.error('Error fetching pending requests count:', err);
      }
    };

    fetchPendingCount();
  }, [getAuthToken]);

  // Fetch pending availability requests count
  useEffect(() => {
    const fetchPendingAvailabilityCount = async () => {
      try {
        const token = await getAuthToken();
        const restOperation = get({
          apiName: 'ChulasEmployeeAPI',
          path: '/v1/admin/scheduling/availability-requests?status=pending',
          options: {
            headers: {
              Authorization: token,
            },
          },
        });
        const response = await restOperation.response;
        const data = await response.body.json();
        setPendingAvailabilityCount(data.requests?.length || 0);
      } catch (err) {
        console.error('Error fetching pending availability requests count:', err);
      }
    };

    fetchPendingAvailabilityCount();
  }, [getAuthToken]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      path: '/users',
      icon: '👥',
      label: 'Users',
    },
    {
      path: '/rewards',
      icon: '🎁',
      label: 'Rewards',
    },
    {
      path: '/valet',
      icon: '🚗',
      label: 'Valet',
    },
    {
      path: '/push-notifications',
      icon: '📱',
      label: 'Push Notifications',
    },
    {
      path: '/announcements',
      icon: '📢',
      label: 'App Announcements',
    },
    {
      path: '/events',
      icon: '📅',
      label: 'Event Calendar',
    },
    {
      path: '/reservations',
      icon: '🍽️',
      label: 'Reservations',
    },
    {
      path: '/scheduling',
      icon: '📊',
      label: 'Labor Planning',
    },
    {
      path: '/specials',
      icon: '🌮',
      label: 'Specials',
    },
    {
      path: '/menu',
      icon: '📋',
      label: 'Menu Manager',
    },
    {
      path: '/mock-data-demo',
      icon: '🔧',
      label: 'Mock Data Demo',
    },
  ];

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && <h2>Chula's Admin</h2>}
        <button onClick={toggleSidebar} className="toggle-button" title={isCollapsed ? 'Expand' : 'Collapse'}>
          {isCollapsed ? '→' : '←'}
        </button>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            title={isCollapsed ? item.label : ''}
          >
            <span className="nav-icon">{item.icon}</span>
            {!isCollapsed && <span className="nav-label">{item.label}</span>}
            {item.path === '/events' && pendingRequestsCount > 0 && (
              <span className="nav-badge">{pendingRequestsCount}</span>
            )}
            {item.path === '/scheduling' && pendingAvailabilityCount > 0 && (
              <span className="nav-badge">{pendingAvailabilityCount}</span>
            )}
          </Link>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;
